import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import ModalReprovacao from '@/modules/reports/presentation/components/ModalReprovacao';
import MyContext from '@/shared/contexts/AppContext';
import RelatoriosAprovacaoFilter from '@/modules/reports/presentation/components/RelatoriosAprovacaoFilter';
import RelatoriosAprovacaoList from '@/modules/reports/presentation/components/RelatoriosAprovacaoList';
import RelatoriosAtividadesNotificacoes from '@/modules/reports/presentation/components/RelatoriosAtividadesNotificacoes';
import { AprovarRelatorio, BuscarRelatoriosParaAprovacao, ReprovarRelatorio, } from '@/modules/reports/application/relatorios-aprovacao.service';
import { Container, Alert } from 'react-bootstrap';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { GetDiretoriaDoUsuario } from '@/modules/structure/diretoria/diretoria.service';
import { ReadDiretoriaDto } from '@/api.types';
import { RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { CarregarPermissoes, ValidarPermissao } from '@/modules/system/auth/application/auth.service';
import { RoleEnum } from '@/modules/system/papeis/enum/role.enum';
import { PermissionEnum } from '@/modules/system/papeis/enum/permission.enum';
import RelatoriosUserLabel from '@/modules/reports/presentation/components/RelatoriosUserLabel';
import RelatoriosEscolhaModuloButton from '@/modules/reports/presentation/components/RelatoriosEscolhaModuloButton';

export default function RelatoriosAprovacaoPage() {
  const context = useContext(MyContext);
  const navigate = useNavigate();

  if (!context) {
    throw new Error('RelatoriosAprovacaoPage deve ser usado dentro de um MyProvider');
  }

  // Estados
  const [loading, setLoading] = useState(true);
  const [relatorios, setRelatorios] = useState<RelatorioAtividadeDto[]>([]);
  const [diretoriaId, setDiretoriaId] = useState<number | null>(null);
  const [diretorias, setDiretorias] = useState<ReadDiretoriaDto[]>([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [relatorioParaReprovar, setRelatorioParaReprovar] = useState<RelatorioAtividadeDto | null>(null);
  const [aproving, setAproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const permissoes = useMemo(() => CarregarPermissoes(), []);
  const isAdminSuporte = useMemo(
    () => ValidarPermissao(permissoes, [{ role: RoleEnum.ADMIN_ACTIONS, level: PermissionEnum.READ }]),
    [permissoes],
  );

  // Carregar diretoria do usuário (diretor regional)
  // Não chamamos GetDiretorias aqui: esse endpoint exige role DEFENSORIA; o diretor tem role DEFENSOR.
  useEffect(() => {
    const carregarDiretoriaUsuario = async () => {
      try {
        const diretoriaUsuario = await GetDiretoriaDoUsuario();

        if (diretoriaUsuario) {
          setDiretoriaId(diretoriaUsuario.id);
          setDiretorias([diretoriaUsuario]);
        } else {
          // Defensor sem diretoria: não tem permissão para aprovação
          setDiretorias([]);
        }
      } catch (error) {
        console.error('Erro ao carregar diretoria do usuário:', error);
        toast.error('Erro ao carregar diretoria');
        setDiretorias([]);
      } finally {
        setLoading(false);
      }
    };

    carregarDiretoriaUsuario();
  }, []);

  // Redirecionar para lista de relatórios se não tiver diretoria (acesso direto pela URL), exceto suporte admin
  useEffect(() => {
    if (!loading && diretoriaId === null && diretorias.length === 0 && !isAdminSuporte) {
      navigate(Links.Relatorios, { replace: true });
    }
  }, [loading, diretoriaId, diretorias.length, navigate, isAdminSuporte]);

  // Carregar relatórios quando diretoria for selecionada
  useEffect(() => {
    if (diretoriaId) {
      carregarRelatorios();
    }
  }, [diretoriaId]);

  const carregarRelatorios = async (filters?: any) => {
    if (!diretoriaId) return;

    try {
      setLoading(true);
      const { statusNaoEnviados, statusEnviados, ...apiFilters } = filters || {};

      // Sem filtro de status = todos os status (enviado, aprovado, reprovado) para o diretor ver tramitação
      const relatoriosData = await BuscarRelatoriosParaAprovacao(diretoriaId, apiFilters);

      /**
       * Partição alinhada à UI (RelatoriosAprovacaoList):
       * - Ainda na fila do diretor / não encaminhados à Corregedoria: aguardando análise (enviado) ou reprovado pela diretoria (!dataAprovacao no reprovado).
       * - Já tramitados após o diretor (encaminhados à Corregedoria ou devolvidos por ela): aprovado pelo diretor, ou reprovado pela Corregedoria (reprovado + dataAprovacao).
       */
      const naFilaDoDiretor = (r: RelatorioAtividadeDto) =>
        r.status === 'enviado' || (r.status === 'reprovado' && !r.dataAprovacao);
      const posDiretorOuCorregedoria = (r: RelatorioAtividadeDto) =>
        r.status === 'aprovado' || (r.status === 'reprovado' && !!r.dataAprovacao);

      let processados = relatoriosData;
      if (statusNaoEnviados) {
        processados = relatoriosData.filter(naFilaDoDiretor);
      } else if (statusEnviados) {
        processados = relatoriosData.filter(posDiretorOuCorregedoria);
      }

      setRelatorios(processados);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (filters: any) => {
    await carregarRelatorios(filters);
  };

  const handleView = (relatorio: RelatorioAtividadeDto) => {
    // Navegar para visualização do relatório
    const params = new URLSearchParams({
      id: String(relatorio.id),
      defensoriaId: String(relatorio.defensoriaId),
      defensoriaName: relatorio.defensoriaName || '',
      mes: String(relatorio.mes).padStart(2, '0'),
      ano: String(relatorio.ano),
      tipo: relatorio.tipo,
      view: 'true', // Flag para modo visualização
    });
    navigate(`${Links.Relatorios_Criar}?${params.toString()}`);
  };

  const handleApprove = async (relatorio: RelatorioAtividadeDto) => {
    if (!diretoriaId) {
      toast.error('Diretoria não selecionada');
      return;
    }

    if (!window.confirm(`Deseja realmente aprovar o relatório de ${relatorio.defensorNome || 'o defensor'}?`)) {
      return;
    }

    try {
      setAproving(true);
      await AprovarRelatorio(relatorio.id, diretoriaId);
      toast.success('Relatório aprovado com sucesso!');
      await carregarRelatorios(); // Recarregar lista
    } catch (error: any) {
      console.error('Erro ao aprovar relatório:', error);
      const mensagem = error?.response?.data?.message || error?.message || 'Erro ao aprovar relatório';
      toast.error(mensagem);
    } finally {
      setAproving(false);
    }
  };

  const handleApproveBatch = async (selecionados: RelatorioAtividadeDto[]) => {
    if (!diretoriaId || selecionados.length === 0) return;
    if (!window.confirm(`Deseja aprovar ${selecionados.length} relatório(s) selecionado(s)?`)) {
      return;
    }
    try {
      setAproving(true);
      let sucesso = 0;
      let falhas = 0;
      for (const r of selecionados) {
        try {
          await AprovarRelatorio(r.id, diretoriaId);
          sucesso++;
        } catch {
          falhas++;
        }
      }
      if (sucesso > 0) {
        toast.success(`${sucesso} relatório(s) aprovado(s) com sucesso.`);
        await carregarRelatorios();
      }
      if (falhas > 0) {
        toast.error(`${falhas} relatório(s) não puderam ser aprovados.`);
      }
    } catch (error: any) {
      const mensagem = error?.response?.data?.message || error?.message || 'Erro ao aprovar em lote.';
      toast.error(mensagem);
    } finally {
      setAproving(false);
    }
  };

  const handleReject = (relatorio: RelatorioAtividadeDto) => {
    setRelatorioParaReprovar(relatorio);
    setShowRejectModal(true);
  };

  const handleConfirmReject = async (justificativa: string) => {
    if (!relatorioParaReprovar || !diretoriaId) return;

    try {
      setRejecting(true);
      await ReprovarRelatorio(relatorioParaReprovar.id, diretoriaId, justificativa);
      toast.success('Relatório reprovado com sucesso!');
      setShowRejectModal(false);
      setRelatorioParaReprovar(null);
      await carregarRelatorios(); // Recarregar lista
    } catch (error: any) {
      console.error('Erro ao reprovar relatório:', error);
      const mensagem = error?.response?.data?.message || error?.message || 'Erro ao reprovar relatório';
      toast.error(mensagem);
    } finally {
      setRejecting(false);
    }
  };

  if (loading && !diretoriaId) {
    return <Loading />;
  }

  return (
    <Fragment>
      <Container fluid>
        <div className="mb-4">
          <div className="d-flex align-items-end justify-content-between gap-2">
            <h2 className="mb-0">Diretoria Regional</h2>
            <div className="d-flex align-items-center gap-2">
              <RelatoriosEscolhaModuloButton />
              <RelatoriosUserLabel className="mb-1" />
            </div>
          </div>
          <p className="text-muted mb-0">
            Gerencie a aprovação dos relatórios de atividades enviados pelos defensores
          </p>
        </div>

        <RelatoriosAtividadesNotificacoes modo="diretoria" />

        {diretorias.length > 1 && (
          <div className="mb-3">
            <label htmlFor="diretoria-select" className="form-label">
              Diretoria Regional:
            </label>
            <select
              id="diretoria-select"
              className="form-select"
              value={diretoriaId || ''}
              onChange={(e) => setDiretoriaId(Number(e.target.value))}
            >
              <option value="">Selecione uma diretoria...</option>
              {diretorias.map((diretoria) => (
                <option key={diretoria.id} value={diretoria.id}>
                  {diretoria.nome}
                </option>
              ))}
            </select>
          </div>
        )}

        {!diretoriaId && diretorias.length > 1 && (
          <Alert variant="info">
            Selecione uma diretoria regional para visualizar os relatórios.
          </Alert>
        )}

        {!diretoriaId && diretorias.length === 0 && (
          <Alert variant="warning">
            <strong>Atenção!</strong> Você não possui uma diretoria regional associada ao seu usuário.
            {isAdminSuporte
              ? ' Com permissão de suporte administrativo, a listagem aqui só preenche quando houver diretoria vinculada ao seu usuário.'
              : ' Entre em contato com o administrador do sistema para configurar sua lotação na diretoria regional.'}
          </Alert>
        )}

        {diretoriaId && (
          <>
            <RelatoriosAprovacaoFilter
              onFilterChange={handleFilterChange}
            />

            <RelatoriosAprovacaoList
              relatorios={relatorios}
              loading={loading}
              onView={handleView}
              onApprove={handleApprove}
              onReject={handleReject}
              onApproveBatch={handleApproveBatch}
              approvingBatch={aproving}
            />
          </>
        )}
      </Container>

      <ModalReprovacao
        show={showRejectModal}
        relatorio={relatorioParaReprovar}
        onHide={() => {
          setShowRejectModal(false);
          setRelatorioParaReprovar(null);
        }}
        onConfirm={handleConfirmReject}
        loading={rejecting}
        contexto="diretoria"
      />
    </Fragment>
  );
}
